import React, { useEffect, useState, useMemo } from 'react';
import {
  useGetFramesQuery,
  useGetFramesStatsQuery,
  useGetViewsQuery,
  useGetSegmentationClassesQuery,
} from '../../redux/slices/views';
import {
  Frame,
  FrameWithSegmentations,
  SegmentationLabel,
  SegmentationParams,
} from 'models/segmentation';
import { Col, Empty, Pagination, Row, Typography, Button, message, Card } from 'antd';
import { AcceptAllButtonWrapper, StatsBarWrapper, StatsContentWrapper } from './styled';
import { PaginationWrapper, PageWrapper } from 'components/ui/wrappers';
import SegmentationItem from './components/SegmentationItem';
import Filters from 'components/Filters/Filters';
import Skeleton from '../Skeleton/Skeleton';
import { useGetSegmentationStatsQuery } from '../../redux/slices/reviews';
import { Decision } from '../../models/reviews';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';

import { useFilterState } from '../../hooks/useFilterState';
import { useSearchParams } from 'react-router-dom';
import RejectModal from './components/RejectModal';
import AcceptModal from './components/AcceptModal';
import { useCreateReviewMutation } from '../../redux/slices/reviews';
import { View } from 'models/views';
import ReviewModal from './components/ReviewModal';

const SegmentationLabels = () => {
  const { filterState, updateFilterTags } = useFilterState();
  // read/write search params for page sync
  const [searchParams, setSearchParams] = useSearchParams();

  const hasAnyFilter = (filterState: any) => {
    if (filterState.view) return true;
    if (filterState.features && filterState.features.length > 0) return true;
    if (filterState.advancedFilters) {
      return Object.values(filterState.advancedFilters).some(
        v =>
          (Array.isArray(v) && v.length > 0) ||
          (typeof v === 'object' && v !== null && Object.keys(v).length > 0 && 'name' in v) ||
          (typeof v === 'string' && v.trim() !== '')
      );
    }
    return false;
  };

  const { data: views } = useGetViewsQuery('');
  const {
    data: segmentationClasses,
    isLoading: isLoadingSegmentationClasses,
    isError: isSegmentationClassesError,
  } = useGetSegmentationClassesQuery({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(6);
  const [acceptIds] = useState<string[]>([]);
  const [isAcceptAllLoading, setIsAcceptAllLoading] = useState(false);
  const [viewOptions, setViewOptions] = useState<AdvancedFilterValue[]>([]);
  const [featureOptions, setFeatureOptions] = useState<AdvancedFilterValue[]>([]);
  const [advancedFiltersOptions] = useState<AdvancedFiltersOptions>({
    datasources: [
      { uuid: 'ABBOTT', name: 'ABBOTT' },
      { uuid: 'ARKHEART', name: 'ARKHEART' },
      { uuid: 'CONWAY', name: 'CONWAY' },
      { uuid: 'DP', name: 'DP' },
      { uuid: 'FSV', name: 'FSV' },
    ],
    dicomICID: '',
    study: '',
    segmentationICID: '',
    frameId: '',
    reviewStatus: [],
    technician: [],
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
  });
  const [visibleSegmentations, setVisibleSegmentations] = useState<Map<string, Set<string>>>(
    new Map()
  );
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [_, setRejectFrameKey] = useState<string | null>(null);
  const [rejectFrameSegmentations, setRejectFrameSegmentations] = useState<SegmentationLabel[]>([]);
  const [createReview] = useCreateReviewMutation();
  const [shouldFetchFrames, setShouldFetchFrames] = useState(false);
  const [localDecisions, setLocalDecisions] = useState<Record<string, Decision | undefined>>({});
  const [removedReviews, setRemovedReviews] = useState<Record<string, boolean>>({});
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptFrameSegmentations, setAcceptFrameSegmentations] = useState<SegmentationLabel[]>([]);
  const [hasAutoAppliedFromQueryParams, setHasAutoAppliedFromQueryParams] = useState(false);
  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Omit<
    SegmentationParams,
    'page' | 'size'
  > | null>(null);

  const { data: segmentationStats, refetch: refetchSegmentationStats } =
    useGetSegmentationStatsQuery();

  const currentFilterSnapshot = useMemo<Omit<SegmentationParams, 'page' | 'size'>>(
    () => ({
      view: filterState.view,
      features: filterState.features,
      includeAllSegmentationLabels: filterState.includeAllSegmentationLabels,
      advancedFilters: {
        datasources: filterState.advancedFilters.datasources,
        dicomICID: filterState.advancedFilters.dicomICID,
        study: filterState.advancedFilters.study,
        annotationSetName: filterState.advancedFilters.annotationSetName,
        segmentationICID: filterState.advancedFilters.segmentationICID,
        // Include frameId and reviewStatus so backend receives frame-specific filters
        frameId: (filterState.advancedFilters as any).frameId,
        reviewStatus: (filterState.advancedFilters as any).reviewStatus,
        technician: filterState.advancedFilters.technician,
        created_at_after: filterState.advancedFilters.created_at_after,
        created_at_before: filterState.advancedFilters.created_at_before,
        updated_at_after: filterState.advancedFilters.updated_at_after,
        updated_at_before: filterState.advancedFilters.updated_at_before,
      },
    }),
    [filterState]
  );

  // Sync initial page from URL (?page=...); default to 1 if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('page');
    const next = qp ? parseInt(qp, 10) : 1;
    if (Number.isFinite(next) && next > 0 && next !== page) {
      setPage(next);
    }
  }, [searchParams]);

  // If ?page is the only meaningful URL state, auto-enable fetching frames with default filters.
  // Filtered URLs are handled by the stricter query-param sync effect below.
  useEffect(() => {
    const hasPage = !!searchParams.get('page');
    const hasFilterParam = [
      'view',
      'features',
      'include_all_segmentation_labels',
      'datasources',
      'dicomICID',
      'frame_id',
      'review_status',
      'study',
      'segmentationICID',
      'technician',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
    ].some(param => searchParams.get(param));
    if (hasPage && !hasFilterParam && !shouldFetchFrames) {
      setAppliedFilters(currentFilterSnapshot);
      setShouldFetchFrames(true);
    }
  }, [searchParams, shouldFetchFrames, currentFilterSnapshot]);

  // Helper to set page in URL (always include ?page=...)
  // Use the current window.location.search so we merge with any query params
  // that may have just been written by other components (avoids race conditions)
  const writePageToURL = (next: number, nextSize?: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(next));
    const sizeToWrite = typeof nextSize === 'number' ? nextSize : size;
    params.set('items_per_page', String(sizeToWrite));
    setSearchParams(params, { replace: true });
  };

  // Sync initial size from URL (?items_per_page=...); default to current `size` if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('items_per_page');
    const next = qp ? parseInt(qp, 10) : undefined;
    if (next && Number.isFinite(next) && next > 0 && next !== size) {
      setSize(next);
    }
  }, [searchParams]);

  // Show frames only after Apply; otherwise show global stats
  useEffect(() => {
    if (!shouldFetchFrames) {
      refetchSegmentationStats();
    }
  }, [shouldFetchFrames, refetchSegmentationStats]);

  const getLocalDecision = (id: string): Decision | undefined => localDecisions[id];

  // State to control when to fetch frames
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  // Use the new getFrames query - only from the last applied filter snapshot.
  const queryParams = useMemo<SegmentationParams>(
    () => ({
      page,
      size,
      ...(appliedFilters || currentFilterSnapshot),
    }),
    [page, size, appliedFilters, currentFilterSnapshot]
  );

  const {
    data: framesPage,
    isLoading: isLoadingGetFrames,
    isFetching,
    refetch: refetchFrames,
  } = useGetFramesQuery(queryParams, {
    skip: !shouldFetchFrames || !appliedFilters,
  });

  // When frames are fetched (filters applied) call the filtered stats endpoint
  const { data: framesStats } = useGetFramesStatsQuery(queryParams, {
    skip: !shouldFetchFrames || !appliedFilters,
  });

  // Reset pagination loading only when the current fetch cycle completes
  useEffect(() => {
    if (!isFetching && isPaginationLoading) {
      setIsPaginationLoading(false);
    }
  }, [isFetching, isPaginationLoading]);

  // Fallback timeout to reset pagination loading if API call takes too long
  useEffect(() => {
    if (isPaginationLoading) {
      const timeout = setTimeout(() => {
        setIsPaginationLoading(false);
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isPaginationLoading]);

  // Map backend response: use segmentationLabels for each frame
  const framesWithSegmentations: FrameWithSegmentations[] = useMemo(
    () =>
      framesPage?.results.map((frame: Frame) => ({
        frame,
        segmentations: frame.segmentationLabels || [],
      })) || [],
    [framesPage]
  );

  const segmentationPage = {
    items: framesPage?.results || [],
    // Prefer frameCount from the filtered frames stats endpoint when available.
    // Older API returned a 'count' on framesPage; newer behavior exposes frameCount in framesStats.stats.
    pages: Math.ceil(((framesStats?.stats?.frameCount ?? framesPage?.count) || 0) / size),
    size: size,
    total: framesStats?.stats?.frameCount ?? framesPage?.count ?? 0,
  };

  useEffect(() => {
    if (framesWithSegmentations.length > 0) {
      const initialVisibility = new Map<string, Set<string>>();
      framesWithSegmentations.forEach(frameGroup => {
        const frameKey = frameGroup.frame.icid;
        const segmentationIds = new Set(frameGroup.segmentations.map(seg => seg.icid));
        initialVisibility.set(frameKey, segmentationIds);
      });
      setVisibleSegmentations(initialVisibility);
    }
  }, [framesWithSegmentations]);

  const handleReviewChange = () => {
    // After reviews change, refresh frames when we're showing frames; otherwise refresh stats
    if (shouldFetchFrames) {
      try {
        refetchFrames();
      } catch (e) {
        // ignore
      }
    } else {
      refetchSegmentationStats();
    }
  };

  const hasAnyQueryParam = useMemo(
    () =>
      [
        'view',
        'features',
        'include_all_segmentation_labels',
        'datasources',
        'dicomICID',
        'frame_id',
        'review_status',
        'study',
        'segmentationICID',
        'technician',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
      ].some(param => searchParams.get(param)),
    [searchParams]
  );

  const isFilterStateSyncedWithQuery = (fs: any) => {
    const viewParam = searchParams.get('view');
    if (viewParam) {
      const matchesView = fs.view && (fs.view.name === viewParam || fs.view.uuid === viewParam);
      if (!matchesView) return false;
    }

    const featuresParam = searchParams.get('features');
    if (featuresParam) {
      const requested = featuresParam
        .split(',')
        .map(f => decodeURIComponent(f))
        .map(f => f.replace(/\+/g, ' '));
      const current = (fs.features || []).map((f: any) => [f.name, f.uuid]).flat();
      const allIncluded = requested.every(r => current.includes(r));
      if (!allIncluded) return false;
    }

    const includeAll = searchParams.get('include_all_segmentation_labels') !== 'false';
    if (fs.includeAllSegmentationLabels !== includeAll) return false;

    const singleStringParams = [
      'dicomICID',
      'study',
      'segmentationICID',
      'technician',
      'created_at_after',
      'created_at_before',
      'updated_at_after',
      'updated_at_before',
    ] as const;

    for (const key of singleStringParams) {
      const qp = searchParams.get(key);
      if (qp) {
        if ((fs.advancedFilters as any)[key] !== qp) return false;
      }
    }

    const datasourceParam = searchParams.get('datasources');
    if (datasourceParam) {
      const ds = fs.advancedFilters?.datasources;
      if (!ds || (ds.name !== datasourceParam && ds.uuid !== datasourceParam)) return false;
    }
    // Frame ID (URL param uses snake_case 'frame_id' while store uses 'frameId')
    const frameIdParam = searchParams.get('frame_id');
    if (frameIdParam) {
      if ((fs.advancedFilters as any).frameId !== frameIdParam) return false;
    }
    // Review status can be comma-separated; ensure all requested statuses are present
    const reviewStatusParam = searchParams.get('review_status');
    if (reviewStatusParam) {
      const requested = reviewStatusParam.split(',');
      const current = (fs.advancedFilters as any).reviewStatus || [];
      const allIncluded = requested.every(r => current.includes(r));
      if (!allIncluded) return false;
    }

    return true;
  };

  useEffect(() => {
    if (!filterState.view) {
      // placeholder if needed later
    }
  }, [filterState.view]);

  useEffect(() => {
    let viewList: View[] = [];
    if (
      views &&
      typeof views === 'object' &&
      'results' in views &&
      Array.isArray((views as any).results)
    ) {
      viewList = (views as { results: View[] }).results;
    } else if (Array.isArray(views)) {
      viewList = views as View[];
    }

    if (viewList.length > 0) {
      const getViewDisplayName = (view: View) => [view.window, view.view].filter(Boolean).join(' ');
      const sortedViews = [...viewList].sort((a, b) =>
        getViewDisplayName(a).localeCompare(getViewDisplayName(b))
      );
      const formattedView = sortedViews.map((view: View) => ({
        uuid: view.uuid,
        name: getViewDisplayName(view),
      }));
      setViewOptions(formattedView);
    } else {
      setViewOptions([]);
    }
  }, [views]);

  // Populate feature options from segmentation-classes endpoint on load
  useEffect(() => {
    if (!segmentationClasses) {
      setFeatureOptions([]);
      return;
    }
    let items: any[] = [];
    if (Array.isArray(segmentationClasses)) {
      items = segmentationClasses as any[];
    } else if (
      typeof segmentationClasses === 'object' &&
      'results' in (segmentationClasses as any) &&
      Array.isArray((segmentationClasses as any).results)
    ) {
      items = (segmentationClasses as any).results;
    }
    const sorted = [...items].sort((a, b) => String(a.value).localeCompare(String(b.value)));
    const features: AdvancedFilterValue[] = sorted.map((item: any) => ({
      uuid: item.uuid,
      name: item.value,
      acronym: item.acronym,
    }));
    setFeatureOptions(features);
  }, [segmentationClasses]);

  const onHandleClear = () => {
    setAppliedFilters(null);
    setShouldFetchFrames(false);
    setPage(1);
    setIsPaginationLoading(false);
    writePageToURL(1);
  };

  const clearSegmentations = () => {
    onHandleClear();
  };

  const onHandleApply = () => {
    setAppliedFilters(currentFilterSnapshot);
    setShouldFetchFrames(true);
    setIsPaginationLoading(false);
    setPage(1);
    // Update active filter tags
    updateFilterTags();

    const params: Record<string, string> = {};

    if (filterState.view?.name) {
      params['view'] = filterState.view.name;
    }
    if (filterState.features.length) {
      params['features'] = filterState.features.map((f: AdvancedFilterValue) => f.name).join(',');
    }
    if (!filterState.includeAllSegmentationLabels) {
      params['include_all_segmentation_labels'] = 'false';
    }

    if (filterState.advancedFilters.datasources) {
      const datasource = filterState.advancedFilters.datasources;
      let datasourceName: string | undefined;
      if (Array.isArray(datasource)) {
        if (datasource.length > 0) {
          datasourceName = datasource[0].name;
        }
      } else if (datasource && typeof datasource === 'object' && 'name' in datasource) {
        datasourceName = (datasource as AdvancedFilterValue).name;
      }
      if (datasourceName) {
        params['datasources'] = datasourceName;
      }
    }

    if (filterState.advancedFilters.dicomICID) {
      params['dicomICID'] = filterState.advancedFilters.dicomICID;
    }
    if (filterState.advancedFilters.study) {
      params['study'] = filterState.advancedFilters.study;
    }
    if (filterState.advancedFilters.segmentationICID) {
      params['segmentationICID'] = filterState.advancedFilters.segmentationICID;
    }
    if (filterState.advancedFilters.technician) {
      params['technician'] = filterState.advancedFilters.technician;
    }
    if (filterState.advancedFilters.created_at_after) {
      params['created_at_after'] = filterState.advancedFilters.created_at_after;
    }
    if (filterState.advancedFilters.created_at_before) {
      params['created_at_before'] = filterState.advancedFilters.created_at_before;
    }
    if (filterState.advancedFilters.updated_at_after) {
      params['updated_at_after'] = filterState.advancedFilters.updated_at_after;
    }
    if (filterState.advancedFilters.updated_at_before) {
      params['updated_at_before'] = filterState.advancedFilters.updated_at_before;
    }
    // New: frame id and review status
    if ((filterState.advancedFilters as any).frameId) {
      params['frame_id'] = (filterState.advancedFilters as any).frameId;
    }
    if (
      (filterState.advancedFilters as any).reviewStatus &&
      Array.isArray((filterState.advancedFilters as any).reviewStatus) &&
      (filterState.advancedFilters as any).reviewStatus.length > 0
    ) {
      params['review_status'] = (filterState.advancedFilters as any).reviewStatus.join(',');
    }

    setSearchParams(params);
    writePageToURL(1);
  };

  const onPaginationChange = (_page: number, pageSize: number) => {
    if (!shouldFetchFrames) return;
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setPage(_page);
    setSize(pageSize);
    writePageToURL(_page, pageSize);
  };

  const onShowSizeChange = (_current: number, newSize: number) => {
    if (!shouldFetchFrames) return;
    setIsPaginationLoading(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setSize(newSize);
    setPage(1);
    writePageToURL(1, newSize);
  };

  // Also trigger loading skeleton if page/size change via URL or programmatically
  useEffect(() => {
    if (shouldFetchFrames) {
      setIsPaginationLoading(true);
    }
  }, [page, size]);

  const setDecisionsFor = (ids: string[], decision: Decision) => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => (next[id] = decision));
      return next;
    });
  };

  const onSelectView = (_value: AdvancedFilterValue) => {
    // no-op hook point
  };

  const allAcceptedOrRejected = framesWithSegmentations.every(frameGroup =>
    frameGroup.segmentations.every(seg => {
      const d = getLocalDecision(seg.icid);
      const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
      const serverAccepted = !wasRemoved && !!seg.isApproved;
      const serverRejected = !wasRemoved && !!seg.isRejected;
      return d === Decision.ACCEPTED || d === Decision.REJECTED || serverAccepted || serverRejected;
    })
  );

  const isAcceptAllDisabled = useMemo(() => {
    return !shouldFetchFrames || !framesWithSegmentations.length;
  }, [shouldFetchFrames, framesWithSegmentations.length]);

  const handleAcceptAll = () => {
    setReviewModalOpen(true);
  };

  const handleConfirmAcceptAll = async () => {
    setIsAcceptAllLoading(true);
    const segmentationsToAccept = framesWithSegmentations
      .flatMap(frameGroup => frameGroup.segmentations)
      .filter(seg => !(seg.isApproved || seg.isRejected || getLocalDecision(seg.icid)));

    if (segmentationsToAccept.length === 0) {
      message.info('All segmentations on this page have already been reviewed.');
      setIsAcceptAllLoading(false);
      setReviewModalOpen(false);
      return;
    }

    const reviewsToCreate = segmentationsToAccept.map(seg => ({
      object_uuid: seg.uuid,
      object_icid: seg.icid,
      object_type: 'SEGMENTATION_LABEL',
      object_view: 'default',
      object_view_id: 0,
      decision: Decision.ACCEPTED,
      comment: '',
      annotation_link: '',
    }));

    try {
      await createReview(reviewsToCreate).unwrap();
      message.success('All remaining segmentations on this page have been accepted.');
      setDecisionsFor(
        segmentationsToAccept.map(s => s.icid),
        Decision.ACCEPTED
      );
    } catch (error) {
      message.error('Failed to accept all segmentations.');
    } finally {
      setIsAcceptAllLoading(false);
      setReviewModalOpen(false);
    }
  };

  const handleSegmentationToggle = (frameKey: string, segmentationId: string, visible: boolean) => {
    setVisibleSegmentations(prevMap => {
      const newMap = new Map(prevMap);
      const frameVisibleSet = new Set(newMap.get(frameKey) || []);

      if (visible) {
        frameVisibleSet.add(segmentationId);
      } else {
        frameVisibleSet.delete(segmentationId);
      }

      newMap.set(frameKey, frameVisibleSet);
      return newMap;
    });
  };

  const handleFrameAccept = (segmentations: SegmentationLabel[]) => {
    // Open accept modal to choose which segmentations to accept
    setAcceptFrameSegmentations(segmentations);
    setAcceptModalOpen(true);
  };

  const handleAcceptModalOk = async (acceptedIds: string[], comment: string) => {
    if (!acceptFrameSegmentations) return;
    try {
      // Only submit reviews for explicitly accepted segmentations.
      const toAccept = acceptFrameSegmentations.filter(seg => acceptedIds.includes(seg.icid));
      if (toAccept.length === 0) {
        message.info('No segmentations selected for acceptance.');
        setAcceptModalOpen(false);
        setAcceptFrameSegmentations([]);
        return;
      }

      const reviewsToCreate = toAccept.map(seg => ({
        object_uuid: seg.uuid,
        object_icid: seg.icid,
        object_type: 'SEGMENTATION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.ACCEPTED,
        comment,
        annotation_link: '',
      }));
      await createReview(reviewsToCreate);
      message.success('Selected segmentations accepted successfully');
      setLocalDecisions(prev => {
        const next = { ...prev };
        toAccept.forEach(seg => {
          next[seg.icid] = Decision.ACCEPTED;
        });
        return next;
      });
    } catch (error) {
      message.error('Failed to submit review');
    }
    setAcceptModalOpen(false);
    setAcceptFrameSegmentations([]);
  };

  const handleFrameReject = (frameKey: string, segmentations: SegmentationLabel[]) => {
    setRejectFrameKey(frameKey);
    setRejectFrameSegmentations(segmentations);
    setRejectModalOpen(true);
  };

  const handleRejectModalOk = async (rejectedIds: string[], comment: string) => {
    if (!rejectFrameSegmentations) return;
    try {
      // Only submit reviews for explicitly rejected segmentations. Do not auto-accept others.
      const toReject = rejectFrameSegmentations.filter(seg => rejectedIds.includes(seg.icid));
      if (toReject.length === 0) {
        message.info('No segmentations selected for rejection.');
        setRejectModalOpen(false);
        setRejectFrameKey(null);
        setRejectFrameSegmentations([]);
        return;
      }

      const reviewsToCreate = toReject.map(seg => ({
        object_uuid: seg.uuid,
        object_icid: seg.icid,
        object_type: 'SEGMENTATION_LABEL',
        object_view: 'default',
        object_view_id: 0,
        decision: Decision.REJECTED,
        comment,
        annotation_link: '',
      }));
      await createReview(reviewsToCreate);
      message.success('Rejected segmentations submitted successfully');
      setLocalDecisions(prev => {
        const next = { ...prev };
        toReject.forEach(seg => {
          next[seg.icid] = Decision.REJECTED;
        });
        return next;
      });
    } catch (error) {
      message.error('Failed to submit review');
    }
    setRejectModalOpen(false);
    setRejectFrameKey(null);
    setRejectFrameSegmentations([]);
  };

  const handleRejectModalCancel = () => {
    setRejectModalOpen(false);
    setRejectFrameKey(null);
    setRejectFrameSegmentations([]);
  };

  const handleLocalDecision = (uuid: string, decision: Decision) => {
    setLocalDecisions(prev => ({ ...prev, [uuid]: decision }));
  };

  const handleLocalDecisionRemove = (id: string) => {
    setLocalDecisions(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleMarkRemoved = (ids: string[]) => {
    setRemovedReviews(prev => {
      const next = { ...prev };
      ids.forEach(id => {
        next[id] = true;
      });
      return next;
    });
    // Also clear any local decisions for those ids
    setLocalDecisions(prev => {
      const next = { ...prev };
      ids.forEach(id => delete next[id]);
      return next;
    });
  };

  // Auto-apply filters and fetch frames if query params are present on initial load
  useEffect(() => {
    if (!hasAutoAppliedFromQueryParams) {
      const hydrated = hasAnyFilter(filterState);
      const synced = isFilterStateSyncedWithQuery(filterState);
      if (hasAnyQueryParam && hydrated && synced) {
        setAppliedFilters(currentFilterSnapshot);
        setShouldFetchFrames(true);
        setHasAutoAppliedFromQueryParams(true);
      }
    }
  }, [
    searchParams,
    filterState,
    currentFilterSnapshot,
    hasAutoAppliedFromQueryParams,
    hasAnyQueryParam,
  ]);

  // ------- NEW: choose which stats to render (filtered frames stats vs global) and normalize keys -------
  const selectedStats = useMemo(() => {
    if (shouldFetchFrames && framesStats?.stats) {
      const s: any = framesStats.stats;
      return {
        total: s.total_found ?? s.totalFound ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.to_be_reviewed ?? s.toBeReviewed ?? 0,
      };
    }
    if (segmentationStats) {
      const s: any = segmentationStats;
      return {
        total: s.totalFound ?? s.total_found ?? 0,
        accepted: s.accepted ?? 0,
        rejected: s.rejected ?? 0,
        toBeReviewed: s.toBeReviewed ?? s.to_be_reviewed ?? 0,
      };
    }
    return null;
  }, [shouldFetchFrames, framesStats, segmentationStats]);

  return (
    <PageWrapper>
      <Filters
        secondaryOptions={featureOptions}
        primaryOptions={viewOptions}
        advancedFiltersOptions={advancedFiltersOptions}
        handlePrimaryOptChange={onSelectView}
        onHandleClear={onHandleClear}
        onHandleApply={onHandleApply}
        isLoadingSegmentationClasses={isLoadingSegmentationClasses}
        isSegmentationClassesError={isSegmentationClassesError}
        clearSegmentations={clearSegmentations}
      />

      {/* Statistics Bar (frames stats after Apply; global stats otherwise) */}
      {selectedStats && (
        <StatsBarWrapper>
          <StatsContentWrapper>
            <Typography.Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.total} Total
            </Typography.Text>
            <Typography.Text type="success" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.accepted} Accepted
            </Typography.Text>
            <Typography.Text type="danger" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.rejected} Rejected
            </Typography.Text>
            <Typography.Text type="warning" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {selectedStats.toBeReviewed} To-be-reviewed
            </Typography.Text>
          </StatsContentWrapper>

          {shouldFetchFrames && framesWithSegmentations && framesWithSegmentations.length > 0 && (
            <AcceptAllButtonWrapper>
              <Button
                type="primary"
                onClick={handleAcceptAll}
                disabled={allAcceptedOrRejected || isAcceptAllLoading || isAcceptAllDisabled}
                loading={isAcceptAllLoading}
                style={{ height: '40px', fontWeight: 'bold', fontSize: '16px' }}
              >
                Accept All On Page
              </Button>
            </AcceptAllButtonWrapper>
          )}
        </StatsBarWrapper>
      )}

      {!shouldFetchFrames ? (
        hasAnyQueryParam ? (
          <Skeleton />
        ) : (
          <Empty
            description={
              <Typography.Text type="secondary">
                Please apply filters to view segmentations.
              </Typography.Text>
            }
          />
        )
      ) : isLoadingGetFrames || isFetching || isPaginationLoading ? (
        <Skeleton />
      ) : framesWithSegmentations && framesWithSegmentations.length > 0 ? (
        <Row gutter={[24, 24]}>
          {framesWithSegmentations.map((frameGroup: FrameWithSegmentations) => {
            const frameKey = frameGroup.frame.icid;
            const frameVisibleSegmentations = visibleSegmentations.get(frameKey) || new Set();
            const allInFrameAccepted = frameGroup.segmentations.every(seg => {
              const d = getLocalDecision(seg.icid);
              const wasRemoved = removedReviews ? !!removedReviews[seg.icid] : false;
              return d === Decision.ACCEPTED || (!wasRemoved && seg.isApproved);
            });

            return (
              <Col key={frameKey} xs={24} sm={24} md={12} lg={8} xl={8}>
                <Card
                  title={
                    <div>
                      <strong>
                        Frame #{frameGroup.frame.index} (ICID: {frameKey})
                      </strong>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {frameGroup.segmentations.length} segmentation
                        {frameGroup.segmentations.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  }
                  style={{ minHeight: 400 }}
                  bordered={false}
                >
                  {frameGroup.segmentations.length === 0 ? (
                    <Typography.Text type="secondary">
                      No segmentations for this frame.
                    </Typography.Text>
                  ) : (
                    <SegmentationItem
                      key={frameGroup.segmentations[0].icid}
                      segmentation={frameGroup.segmentations[0]}
                      frameFile={frameGroup.frame.fileUrl}
                      review={undefined}
                      acceptMe={acceptIds.includes(frameGroup.segmentations[0].icid)}
                      allSegmentations={frameGroup.segmentations}
                      onSegmentationToggle={(segmentationId, visible) =>
                        handleSegmentationToggle(frameKey, segmentationId, visible)
                      }
                      visibleSegmentations={frameVisibleSegmentations}
                      onFrameAccept={() => handleFrameAccept(frameGroup.segmentations)}
                      onFrameReject={() => handleFrameReject(frameKey, frameGroup.segmentations)}
                      frameReviewed={allInFrameAccepted}
                      onLocalDecision={handleLocalDecision}
                      onLocalDecisionRemove={handleLocalDecisionRemove}
                      getLocalDecision={getLocalDecision}
                      onReviewChange={handleReviewChange}
                      removedReviews={removedReviews}
                      onMarkRemoved={handleMarkRemoved}
                      segmentationClassOptions={featureOptions}
                      isRejected={frameGroup.segmentations.some(s => {
                        const d = getLocalDecision(s.icid);
                        return d === Decision.REJECTED || s.isRejected;
                      })}
                    />
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Empty
          description={
            <Typography.Text type="secondary">
              No segmentations found for the selected filters.
            </Typography.Text>
          }
        />
      )}

      {shouldFetchFrames && framesWithSegmentations && framesWithSegmentations.length > 0 && (
        <PaginationWrapper>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pagination
              total={segmentationPage.total}
              defaultPageSize={size}
              current={page}
              onShowSizeChange={onShowSizeChange}
              onChange={onPaginationChange}
              showTotal={total => `Total ${total} items`}
            />
            {!framesStats && (
              <Button
                onClick={() => {
                  const nextPage = page + 1;
                  onPaginationChange(nextPage, size);
                }}
                disabled={
                  !(
                    page < segmentationPage.pages ||
                    (framesPage?.results && framesPage.results.length === size)
                  )
                }
              >
                Next
              </Button>
            )}
          </div>
        </PaginationWrapper>
      )}

      <RejectModal
        isModalOpen={rejectModalOpen}
        handleCancel={handleRejectModalCancel}
        handleOk={handleRejectModalOk}
        segmentations={rejectFrameSegmentations}
        initialRejectedIds={rejectFrameSegmentations
          .filter(seg => {
            const d = getLocalDecision(seg.icid);
            return d === Decision.REJECTED || seg.isRejected;
          })
          .map(seg => seg.icid)}
      />
      <AcceptModal
        isModalOpen={acceptModalOpen}
        handleCancel={() => {
          setAcceptModalOpen(false);
          setAcceptFrameSegmentations([]);
        }}
        handleOk={handleAcceptModalOk}
        segmentations={acceptFrameSegmentations}
        initialAcceptedIds={acceptFrameSegmentations
          .filter(seg => {
            const d = getLocalDecision(seg.icid);
            return d === Decision.ACCEPTED || seg.isApproved;
          })
          .map(seg => seg.icid)}
      />
      <ReviewModal
        isModalOpen={isReviewModalOpen}
        handleOk={handleConfirmAcceptAll}
        handleCancel={() => setReviewModalOpen(false)}
        loading={isAcceptAllLoading}
      />
    </PageWrapper>
  );
};

export default SegmentationLabels;
